import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquareWarning, X, Send } from "lucide-react";
import { client } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const HIDDEN_PATHS = ["/privacy", "/terms", "/cookies", "/disclaimer"];

export const ReportErrorPill = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [wantsFollowup, setWantsFollowup] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  if (!user) return null;
  if (HIDDEN_PATHS.some((p) => loc.pathname.startsWith(p))) return null;

  const submit = async (e) => {
    e.preventDefault();
    const msg = message.trim();
    if (msg.length < 1) { toast.error("Please describe the problem first."); return; }
    setSending(true);
    try {
      await client.post("/report", {
        url: window.location.href,
        message: msg,
        wants_followup: wantsFollowup,
        reply_email: wantsFollowup ? email.trim() : null,
      });
      toast.success("Thanks! Your report has been sent.");
      setMessage(""); setWantsFollowup(false); setOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 429
        ? "You've sent a few reports already - please try again later."
        : "Could not send your report. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60] print:hidden" data-testid="report-error-widget">
      {open ? (
        <form onSubmit={submit} data-testid="report-error-form"
          className="w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_16px_50px_rgba(0,0,0,0.18)]">
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-ink">
              <MessageSquareWarning className="h-4 w-4 text-volt" /> Feedback
            </span>
            <button type="button" data-testid="report-close-btn" onClick={() => setOpen(false)}
              className="text-slate-400 transition-colors hover:text-ink" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4">
            <textarea
              data-testid="report-message-input" autoFocus value={message}
              onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={4}
              placeholder="Share feedback or report a problem…"
              className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none transition-colors focus:border-ink focus:bg-white"
            />
            <label className="mt-3 flex items-start gap-2 text-sm text-slate-600" data-testid="report-followup-label">
              <input type="checkbox" data-testid="report-followup-checkbox" checked={wantsFollowup}
                onChange={(e) => setWantsFollowup(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-volt" />
              <span>Email me a follow-up about this</span>
            </label>
            {wantsFollowup && (
              <input
                type="email" data-testid="report-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition-colors focus:border-ink focus:bg-white"
              />
            )}
            <button type="submit" data-testid="report-submit-btn" disabled={sending}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-slate-800 disabled:opacity-60">
              <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send report"}
            </button>
          </div>
        </form>
      ) : (
        <button data-testid="report-error-pill" onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-ink shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-200 hover:-translate-y-0.5 hover:border-ink">
          <MessageSquareWarning className="h-4 w-4 text-volt" /> Feedback
        </button>
      )}
    </div>
  );
};
